/**
 * Single source of truth for mapping API MessageType → ChatMessageEntityType.
 * Used by both the API response transformer and WebSocket stream handlers
 * to ensure consistent type resolution across reload and live-streaming flows.
 */

import type { ChatMessageEntityType } from '../store/slices/chat-message-entity.interface'
import { logger } from '../utils/logger.util'
import type { MessageType } from './message.interface'

type EntityTypeOrExcluded = ChatMessageEntityType | null

const API_MESSAGE_TYPE_TO_ENTITY_TYPE: Record<MessageType, EntityTypeOrExcluded> = {
  MESSAGE: 'message',
  NOTE: 'note',
  TOOL_CALL: 'tool-call',
  TOOL_RESULT: 'tool-call',
  AGENT_REPORT: 'agent-report',
  DELEGATION: 'delegation',
  LLM_CALL: null,
  AGENT_START: null,
}

const isKnownMessageType = (messageType: string): messageType is MessageType =>
  Object.hasOwn(API_MESSAGE_TYPE_TO_ENTITY_TYPE, messageType)

export const resolveEntityType = (messageType: MessageType): ChatMessageEntityType => {
  if (!isKnownMessageType(messageType)) {
    logger.warn('Unknown MessageType from backend, defaulting to "message"', { messageType })
    return 'message'
  }
  return API_MESSAGE_TYPE_TO_ENTITY_TYPE[messageType] ?? 'message'
}

export const isExcludedMessageType = (messageType: MessageType): boolean =>
  API_MESSAGE_TYPE_TO_ENTITY_TYPE[messageType] === null
